import { cx } from "@/lib/utils/cx"

// Shape-matched placeholders: the room's silhouette while the data arrives,
// never a spinner. `animate-pulse` is the app's existing loading idiom.

export function Bone({ className, width }: { className?: string; width?: number }) {
  return (
    <div
      className={cx("rounded-md bg-zinc-100 animate-pulse motion-reduce:animate-none", className)}
      style={width != null ? { width: `${width * 4}px` } : undefined}
      aria-hidden
    />
  )
}

export function ListSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <ul className="divide-y divide-zinc-100" aria-busy="true" aria-label="Loading tasks">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="px-3.5 py-3 flex items-start gap-2.5">
          <Bone className="h-[22px] w-14" />
          <div className="flex-1 space-y-1.5">
            <Bone className="h-3.5 w-2/3" />
            <Bone className="h-3 w-1/3" />
          </div>
          <Bone className="h-3.5 w-12" />
        </li>
      ))}
    </ul>
  )
}

export function CardSkeleton() {
  return (
    <div className="rounded-2xl border border-zinc-200/80 bg-white overflow-hidden" aria-busy="true" aria-label="Loading the task">
      <div className="px-6 pt-5 pb-4 space-y-2.5">
        <Bone className="h-[22px] w-24" />
        <Bone className="h-6 w-56" />
        <Bone className="h-3.5 w-80" />
        <Bone className="h-3.5 w-64" />
        <Bone className="h-2.5 w-full mt-2" />
      </div>
      <div className="border-t border-zinc-200/80 px-6 py-5 space-y-4">
        <Bone className="h-12 w-52 rounded-lg" />
        <Bone className="h-11 w-full rounded-lg" />
        <div className="flex flex-wrap gap-2">
          {[28, 32, 30, 34].map((w, i) => (
            <Bone key={i} className="h-11 rounded-full" width={w} />
          ))}
        </div>
      </div>
    </div>
  )
}

/** The context rail: calendar, messages, estimate — still on their way. */
export function RailSkeleton() {
  return (
    <div className="rounded-2xl border border-zinc-200/80 bg-white overflow-hidden divide-y divide-zinc-200/80" aria-busy="true">
      <div className="px-5 py-5">
        <PaneSkeleton />
      </div>
      <div className="px-5 py-5">
        <ThreadSkeleton />
      </div>
    </div>
  )
}

/** The calendar or the estimate, still on its way. */
export function PaneSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true">
      <Bone className="h-4 w-28" />
      <div className="flex gap-1.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <Bone key={i} className="h-11 w-12 rounded-lg" />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {Array.from({ length: 9 }).map((_, i) => (
          <Bone key={i} className="h-10 rounded-lg" />
        ))}
      </div>
    </div>
  )
}

/** A message thread, still on its way. */
export function ThreadSkeleton() {
  return (
    <div className="space-y-3 py-2" aria-busy="true">
      <Bone className="h-3 w-40 mx-auto" />
      <Bone className="h-9 w-2/3 rounded-2xl" />
      <Bone className="h-9 w-1/2 rounded-2xl ml-auto" />
      <Bone className="h-11 w-full rounded-lg mt-2" />
    </div>
  )
}
